import {
  Component,
  HostBinding,
  Input,
  ChangeDetectorRef,
  ElementRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Inject,
  ViewChildren,
  QueryList,
} from '@angular/core';

import update from 'immutability-helper';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { filterDict } from 'shared/utils/functools-map';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AnimationsRenderService } from 'shared/animations/animations-render/animations-render.service';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { DomSanitizer } from '@angular/platform-browser';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonWindow } from 'extensions/oy/models/common-window.model';
import { DeviceType } from 'app.config';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';


interface HostStyles {
  backgroundColor: string;
  justifyContent: string;
  alignItems: string;
  alignContent: string;
  marginTop: string;
  marginRight: string;
  marginLeft: string;
  marginBottom: string;
  paddingTop: string;
  paddingRight: string;
  paddingLeft: string;
  paddingBottom: string;
  position: string;
}

interface SlotResponsiveConfig extends Partial<Exclude<Slot, 'responsiveConfig'>> {
  id: string;
}

export interface Slot {
  hidden: boolean;
  'flex-grow': string;
  'flex-shrink': string;
  'flex-basis': string;
  'flex-direction': string;
  width: string;
  height: string;
  'min-height': string;
  'max-height': string;
  'min-width': string;
  'max-width': string;
  responsiveConfig: [SlotResponsiveConfig];
}

function toFlex(value: string) {
  switch (value) {
    case 'start': return 'flex-start';
    case 'end': return 'flex-end';
    case 'spaceBetween': return 'space-between';
    case 'spaceAround': return 'space-around';
    case 'spaceEvenly': return 'space-evenly';
    default: return value;
  }
}

const BANNER_KEY = 'closedBanners';

@Component({
  selector: 'slice-sticky-render',
  templateUrl: './slice-sticky-render.component.html',
  styleUrls: ['./slice-sticky-render.component.scss']
})
export class SliceStickyRenderComponent
  extends UnsubscribeComponent
  implements OnInit, AfterViewInit {

  @Input() public slice?: IdtComponent;

  @HostBinding('style.background-color')
  public backgroundColor!: string;

  @HostBinding('style.justify-content')
  public justifyContent!: string;

  @HostBinding('style.align-items')
  public alignItems!: string;

  @HostBinding('style.align-content')
  public alignContent!: string;

  @HostBinding('style.margin-top')
  public marginTop!: string;

  @HostBinding('style.margin-right')
  public marginRight!: string;

  @HostBinding('style.margin-left')
  public marginLeft!: string;

  @HostBinding('style.margin-bottom')
  public marginBottom!: string;

  @HostBinding('style.padding-top')
  public paddingTop!: string;

  @HostBinding('style.padding-right')
  public paddingRight!: string;

  @HostBinding('style.padding-left')
  public paddingLeft!: string;

  @HostBinding('style.padding-bottom')
  public paddingBottom!: string;

  @HostBinding('style.flex-direction')
  public get flexDirection() {
    return isDefined(this.slice) &&
      isDefined(this.slice.settings) &&
      isDefined(this.slice.settings.general) &&
      isDefined(this.slice.settings.general.flexDirection) ? this.slice.settings.general.flexDirection : 'column';
  }

  @HostBinding('style.flex-wrap')
  public get flexWrap() {
    return isDefined(this.slice) &&
      isDefined(this.slice.settings) &&
      isDefined(this.slice.settings.general) &&
      isDefined(this.slice.settings.general.flexWrap) ? this.slice.settings.general.flexWrap : 'initial';
  }

  @HostBinding('style.border')
  public get border() {
    let result = 'none';
    if (isDefined(this.slice) && isDefined(this.slice.settings) && isDefined(this.slice.settings.styles)) {
      const { borderStyle, borderWidth, borderColor } = this.slice.settings.styles;
      result = `${borderWidth} ${borderStyle} ${borderColor}`;
    }
    return result;
  }

  @HostBinding('style.background-image')
  public get backgroundImage() {
    const image = this.slice?.settings?.styles?.backgroundImage?.image;

    if (image) {
      const assetsUrl = this.renderConfigService.config.assetsUrl;

      const akamaiOptions: AmandaOptions = {
        isWidthParamEnabled: this.renderConfigService.config.amandaImageVideoManagerEnabled,
        width: this.assetsSharedService.getWidth(this.el)
      };

      const absoluteUrl = this.assetsSharedService.getAssetUrl(image, assetsUrl, akamaiOptions);

      return this.domSanitizer.bypassSecurityTrustStyle(`url(${absoluteUrl})`);
    }

    return undefined;
  }

  @HostBinding('style.background-attachment')
  public get backgroundAttachment() {
    if (this.slice?.settings?.styles?.backgroundImage?.fixed) {
      return 'fixed';
    }

    return undefined;
  }

  @HostBinding('style.background-size')
  public get backgroundSize() {
    return this.slice?.settings?.styles?.backgroundImage?.size;
  }

  @HostBinding('style.background-repeat')
  public get backgroundRepeat() {
    return this.slice?.settings?.styles?.backgroundImage?.image ? 'no-repeat' : undefined;
  }

  @HostBinding('style.position')
  public position!: string;

  @HostBinding('style.top')
  public top!: string | number;

  @HostBinding('style.bottom')
  public bottom!: string | number;

  @ViewChildren('contentSlots', {read: ElementRef}) public contentSlots?: QueryList<ElementRef>;
  @ViewChildren('slotsWrapper') public slotsWrappers?: QueryList<ElementRef>;

  public slots: Slot[] = [];
  public generalSettings: any;
  public currentSlot: number = 0;
  public currentBar: number = -1;

  /** Indicates if the current sticky has to keep closed. */
  public keepClosed: boolean = false;
  /** Indicates if the device is tablet or bigger */
  public isTabletOrBigger: boolean = false;
  /** Animation time for texts and slot changes */
  public animationTime: number = 2;
  /** Slot change animation interval */
  public slotIntervalSubscription?: Subscription;
  /** Current background color corresponding to active slot */
  public slotBackgroundColor?: string;
  /** Indicates if text should be animated in one line */
  public shouldAnimateText: boolean = false;

  private animationsEventsDestroy?: () => void;

  constructor(
    @Inject('Window') private window: CommonWindow,
    private domSanitizer: DomSanitizer,
    private assetsSharedService: AssetsSharedService,
    private breakpointObserver: BreakpointObserver,
    private renderConfigService: RenderConfigurationService,
    private animationsRenderService: AnimationsRenderService,
    private cd: ChangeDetectorRef,
    private el: ElementRef,
  ) {
    super();
  }

  public async ngOnInit() {
    if (this.slice && this.slice.settings) {
      this.checkVisibility();
      if (this.slice.settings.animations) {
        this.animationsEventsDestroy = await this.animationsRenderService.setUp(this.el, this.slice.settings.animations);
      }

      this.setDefaultHostStyles();
      this.breakpointObserver.observe(this.renderConfigService.config.breakpoints.map((breakpoint) => {
        return breakpoint.bp;
      }))
        .pipe(
          takeUntil(this.clearSubscriptions$),
        )
        .subscribe((breakpointState) => {
          this.onMediaChange(breakpointState);
          this.cd.markForCheck();
        });
    }
  }

  public ngAfterViewInit() {
    this.generalSettings = this.slice?.settings?.general;
    requestAnimationFrame(() => {
      this.checkIfTextShouldBeAnimated();
    });
    this.initAnimations();
  }

  public onMediaChange(breakpointState: BreakpointState) {
    const matches = filterDict(breakpointState.breakpoints, (key: string, value: any) => {
      return !!value;
    });

    const matchKeys = Object.keys(matches);

    // Get active device and adjust animation times to it.
    const device = this.renderConfigService.config?.breakpoints?.find(breakpoint => breakpoint.bp === matchKeys[0]);
    if (isDefined(device)) {
      this.isTabletOrBigger = !device.device.includes(DeviceType.MOBILE);
      this.animationTime = (this.isTabletOrBigger
        ? this.slice?.settings?.general?.animationTimeDesktop
        : this.slice?.settings?.general?.animationTimeMobile) || 2;
      this.checkIfTextShouldBeAnimated();
      this.initAnimations();
    }

    if (matchKeys.length) {
      const mainMatch = matchKeys[0];
      const responsiveSlots = this.getResponsiveSlots(mainMatch).map((slot: Slot) => {
        const responsiveConfig = slot.responsiveConfig.find((rc: SlotResponsiveConfig) => rc.id === mainMatch);
        const newSlot = Object.keys(slot).reduce((acc, key) => ({
          ...acc,
          [key]: isDefined(responsiveConfig) ? responsiveConfig[key as keyof SlotResponsiveConfig] : slot[key as keyof Slot]
        }), slot) as Slot;
        return newSlot;
      });
      this.slots = responsiveSlots;
    } else {
      this.setDefaultHostStyles();
      this.slots = this.getDefaultSlotStructure(this.slice?.settings.slots);
    }
  }

  public close() {
    if (this.slice?.settings?.general?.keepClosedSession) {
      const bannerName = this.slice?.id;
      const closedBanners = this.window.sessionStorage.getItem(BANNER_KEY);
      const newClosedBanners = closedBanners
        ? `${closedBanners},${bannerName}`
        : bannerName;
      this.window.sessionStorage.setItem(BANNER_KEY, newClosedBanners);
    }
    this.el.nativeElement.remove();
    // Implementar, si se encuentra, otra solución para eliminar el propio componente
    this.ngOnDestroy();
  }

  public ngOnDestroy() {
    if (this.animationsEventsDestroy) {
      this.animationsEventsDestroy();
    }

    super.ngOnDestroy();
  }

  public stylePropOrDefault(propValue: string, defaultValue: string): string {
    return isDefined(propValue) ? propValue : defaultValue;
  }

  public initAnimations() {
    this.currentSlot = 0;
    this.setCurrentSlotBackgroundColor();
    if (!this.generalSettings?.animatedTexts) { return; }
    if (isDefined(this.slotIntervalSubscription)) { this.slotIntervalSubscription.unsubscribe(); }
    const slotInterval = interval(this.animationTime * 1000 || 2000);
    this.slotIntervalSubscription = slotInterval.pipe(takeUntil(this.clearSubscriptions$)).subscribe(() => {
      this.currentSlot = (this.currentSlot + 1) % this.slots.length;
      this.checkIfTextShouldBeAnimated();
      this.setCurrentSlotBackgroundColor();
      this.cd.detectChanges();
    });
  }

  private setDefaultHostStyles() {
    this.setHostStyles({
      backgroundColor: this.slice?.settings.styles.bgColor,
      justifyContent: this.slice?.settings.general.justifyContent,
      alignItems: this.slice?.settings.general.alignItems,
      alignContent: this.slice?.settings.general.alignContent,
      marginTop: this.slice?.settings.styles.marginTop,
      marginRight: this.slice?.settings.styles.marginRight,
      marginLeft: this.slice?.settings.styles.marginLeft,
      marginBottom: this.slice?.settings.styles.marginBottom,
      paddingTop: this.slice?.settings.styles.paddingTop,
      paddingRight: this.slice?.settings.styles.paddingRight,
      paddingLeft: this.slice?.settings.styles.paddingLeft,
      paddingBottom: this.slice?.settings.styles.paddingBottom,
      position: this.slice?.settings.general.position
    });
  }

  private setHostStyles(styles: HostStyles) {
    this.backgroundColor = styles.backgroundColor;
    this.justifyContent = toFlex(styles.justifyContent);
    this.alignItems = toFlex(styles.alignItems);
    this.alignContent = toFlex(styles.alignContent);
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
    this.paddingTop = styles.paddingTop;
    this.paddingRight = styles.paddingRight;
    this.paddingLeft = styles.paddingLeft;
    this.paddingBottom = styles.paddingBottom;
    this.position = styles.position === 'top' || styles.position === 'bottom' ? 'fixed' : 'relative';
    this.top = styles.position === 'top' ? 0 : 'auto';
    this.bottom = styles.position === 'bottom' ? 0 : 'auto';
  }

  private getDefaultSlotStructure(slots: Slot[]): Slot[] {
    return slots.map((slot) => {
      return update(slot, {
        width: { $set: slot.width },
        hidden: { $set: false }
      }) as Slot;
    });
  }

  private getResponsiveSlots(breakpoint: string) {
    const defaultBpSettings = {
      marginTop: this.slice?.settings.styles.marginTop,
      marginBottom: this.slice?.settings.styles.marginBottom,
      marginLeft: this.slice?.settings.styles.marginLeft,
      marginRight: this.slice?.settings.styles.marginRight,
      paddingTop: this.slice?.settings.styles.paddingTop,
      paddingBottom: this.slice?.settings.styles.paddingBottom,
      paddingLeft: this.slice?.settings.styles.paddingLeft,
      paddingRight: this.slice?.settings.styles.paddingRight,
      structure: [
        this.slice?.settings.slots.map(() => 1)
      ]
    };

    const responsiveSettings: ResponsiveSetting[] = this.slice?.settings.styles.responsiveConfig || [];

    const breakpointStyles = responsiveSettings.find((responsiveSetting) => {
      return responsiveSetting.bp === breakpoint || responsiveSetting.id === breakpoint;
    }) || {} as ResponsiveSetting;

    this.setHostStyles({
      backgroundColor: this.slice?.settings.styles.bgColor,
      justifyContent: this.slice?.settings.general.justifyContent,
      alignItems: this.slice?.settings.general.alignItems,
      alignContent: this.slice?.settings.general.alignContent,
      marginTop: breakpointStyles?.marginTop || defaultBpSettings.marginTop,
      marginRight: breakpointStyles?.marginRight || defaultBpSettings.marginRight,
      marginLeft: breakpointStyles?.marginLeft || defaultBpSettings.marginLeft,
      marginBottom: breakpointStyles?.marginBottom || defaultBpSettings.marginBottom,
      paddingTop: breakpointStyles?.paddingTop || defaultBpSettings.paddingTop,
      paddingRight: breakpointStyles?.paddingRight || defaultBpSettings.paddingRight,
      paddingLeft: breakpointStyles?.paddingLeft || defaultBpSettings.paddingLeft,
      paddingBottom: breakpointStyles?.paddingBottom || defaultBpSettings.paddingBottom,
      position: this.slice?.settings.general.position
    });

    return this.getSlotsStructure(this.slice?.settings.slots, breakpointStyles.structure || defaultBpSettings.structure);
  }

  private getSlotsStructure(sliceSlots: {}[], structure: ResponsiveSetting['structure']): Slot[] {
    const slots = [...sliceSlots];
    const finalSlots: Slot[] = [];

    slots.forEach(() => {
      structure.forEach((row) => {
        const columns = row.length;
        const hiddenColumns = row.filter((it) => !it);

        row.forEach((column) => {
          if (slots.length) {
            const slot = slots.shift() as Slot;

            const visibleColumns = columns - hiddenColumns.length;
            const size = visibleColumns && column ? `calc(100% / ${visibleColumns})` : 'auto';

            let style = {};

            if (columns !== sliceSlots.length) {
              if (this.slice?.settings?.general?.flexDirection === 'column' && (slot?.height ?? 'auto') === 'auto') {
                style = { height: { $set: size } };
              } else if ((slot?.width ?? 'auto') === 'auto') {
                style = { width: { $set: size } };
              }
            }

            finalSlots.push(update(slot, {
              hidden: { $set: !column },
              ...style
            }));
          }
        });
      });
    });

    return finalSlots;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  /**
   * Check if the sticky component has checked the keepClosedSession flag and was previously closed.
   *
   * @returns If the sticky component has checked the keepClosedSession flag and was previously closed.
   */
  private checkVisibility() {
    if (this.slice?.settings?.general?.keepClosedSession) {
      const closedBanners = this.window.sessionStorage.getItem(BANNER_KEY);
      this.keepClosed = !!closedBanners && closedBanners.split(',').indexOf(`${this.slice?.id}`) >= 0;
    }
  }

  /**
   * Sets the current slice sticky background color to the one selected for current slot
   */
  private setCurrentSlotBackgroundColor() {
    const slotBackgroundColors = this.slice?.settings?.styles?.slotBackgroundColors;
    if (isDefined(slotBackgroundColors)) {
      this.slotBackgroundColor = slotBackgroundColors[this.currentSlot]?.backgroundColor;
      this.cd.detectChanges();
    }
  }

  /**
   * Checks if the text should be animated depending of its size.
   */
  private checkIfTextShouldBeAnimated() {
    const animatedTextActive = !!this.slice?.settings?.general?.showInOneLine;
    const wrapperWidth = this.slotsWrappers?.get(this.currentSlot)?.nativeElement?.offsetWidth;
    const textWidth = this.contentSlots?.get(this.currentSlot)?.nativeElement?.scrollWidth;
    if (wrapperWidth && textWidth) {
      this.shouldAnimateText = animatedTextActive && (textWidth > wrapperWidth);
    }
    this.cd.detectChanges();
  }

}
